/** @jsx tsx */
import { WidgetProperties } from "@dojo/framework/core/interfaces";
import { tsx } from "@dojo/framework/core/vdom";
import { WidgetBase } from "@dojo/framework/core/WidgetBase";
import { IScaleObjects } from "../../store/ApiContracts";
import { PageObjectScalePointType } from "../interfaces/model/enumerations/PageObjectScalePoints";
import { PageObjectType } from "../interfaces/model/Types";
import { isExpandFromCenter, maintainAspectRatio } from "../keyUtil";
import { CsiDrag, IDragResults, IPosition } from "../meta/Drag/index";
import { getZoomValue } from "../selectors/index";

/** A coordinate interface */
interface ICoordinate {
    x: number;
    y: number;
}

/** Cropping handler render properties */
export interface ICroppingHandlerRenderProps extends WidgetProperties {
    /** Scale point type */
    position: PageObjectScalePointType;
    /** Handler position coordinate */
    coordinate: ICoordinate;

    /** Page Object */
    pageObject: PageObjectType;

    /**
     * Object scale callback.
     */
    scaleObjects: IScaleObjects;

    /**
     * Set all aspect ratios callback
     */
    setAllAspectRatios(transient: boolean): void;
}

/**
 * Picture edit cropping handler
 */
export class CroppingHandler extends WidgetBase<ICroppingHandlerRenderProps> {
	/** dragging flag */
	private _dragging?: boolean;

	constructor() {
		super();
	}

	/**
     * Remove all pointer event listeners on shape
     */
	protected onDetach(): void {
		this.meta(CsiDrag)
			.remove(this.properties.key as string);
	}

	protected _onPointerDrag(drag: IDragResults) {
		// if dragging is not yet started return
		if (!this._dragging && !drag.isDragging) {
			return;
		}

		if (this._dragging && !drag.isDragging) {
			// if previously dragging was true and now dragResults say it is false then dragging is stopped.
			this._dragging = false;
		} else {
			this._dragging = true;
		}

		const pageObject = this.properties.pageObject;

		// if dragging is continued we want to keep these changes as transient.
		const isTransient = this._dragging;
		const isMaintainAspectRatio = maintainAspectRatio(drag.keyData) || pageObject.noChangeAspect;
		const expandFromCenter = isExpandFromCenter(drag.keyData);
		const currentPosition = drag.positionInCanvas as IPosition;
		this.properties.scaleObjects(this.properties.position,
			drag.delta, currentPosition, expandFromCenter || false, isMaintainAspectRatio || false,
			pageObject.shapeId, null, isTransient, false);

		if (!isTransient) {
			this.properties.setAllAspectRatios(isTransient);
		}
	}

	/**
     * Override WidgetBase#render to produce a virtual DOM tree.
     * @returns {HNode} Each time render() executes, it should build the entire virtual DOM tree.
     */
	protected render() {
		const {
			coordinate,
			key,
			position,
		} = this.properties;

		const handleSize = getZoomValue(10);
		const strokeWidth = 5;
		const halfStrokeWidth = getZoomValue(strokeWidth / 2);
		const { x, y } = coordinate;
		let firstPoint: ICoordinate;
		let secondPoint: ICoordinate = { x: x + halfStrokeWidth, y: y + halfStrokeWidth };
		let thirdPoint: ICoordinate;

		// Based on handler position, decide on coordinate values for handler path
		switch (position) {
			case PageObjectScalePointType.CORNER_LEFT_TOP:
				firstPoint = { x: x + halfStrokeWidth, y: y + handleSize + halfStrokeWidth };
				secondPoint = { x: x + halfStrokeWidth, y: y + halfStrokeWidth };
				thirdPoint = { x: x + handleSize + halfStrokeWidth, y: y + halfStrokeWidth };
				break;
			case PageObjectScalePointType.SIDE_TOP:
				firstPoint = { x: x - (handleSize / 2), y: y + halfStrokeWidth };
				secondPoint = { x: x + halfStrokeWidth, y: y + halfStrokeWidth };
				thirdPoint = { x: x + (handleSize / 2), y: y + halfStrokeWidth };
				break;
			case PageObjectScalePointType.SIDE_BOTTOM:
				firstPoint = { x: x - (handleSize / 2), y: y - halfStrokeWidth };
				secondPoint = { x, y: y - halfStrokeWidth };
				thirdPoint = { x: x + (handleSize / 2), y: y - halfStrokeWidth };
				break;
			case PageObjectScalePointType.CORNER_RIGHT_TOP:
				firstPoint = { x: x - handleSize - halfStrokeWidth, y: y + halfStrokeWidth };
				secondPoint = { x: x - halfStrokeWidth, y: y + halfStrokeWidth };
				thirdPoint = { x: x - halfStrokeWidth, y: y + handleSize + halfStrokeWidth };
				break;
			case PageObjectScalePointType.SIDE_LEFT:
				firstPoint = { x: x + halfStrokeWidth, y: y - (handleSize / 2) };
				secondPoint = { x: x + halfStrokeWidth, y };
				thirdPoint = { x: x + halfStrokeWidth, y: y + (handleSize / 2) };
				break;
			case PageObjectScalePointType.SIDE_RIGHT:
				firstPoint = { x: x - halfStrokeWidth, y: y - (handleSize / 2) };
				secondPoint = { x: x - halfStrokeWidth, y };
				thirdPoint = { x: x - halfStrokeWidth, y: y + (handleSize / 2) };
				break;
			case PageObjectScalePointType.CORNER_LEFT_BOTTOM:
				firstPoint = { x: x + halfStrokeWidth, y: y - handleSize - halfStrokeWidth };
				secondPoint = { x: x + halfStrokeWidth, y: y - halfStrokeWidth };
				thirdPoint = { x: x + handleSize + halfStrokeWidth, y: y - halfStrokeWidth };
				break;
			case PageObjectScalePointType.CORNER_RIGHT_BOTTOM:
				firstPoint = { x: x - halfStrokeWidth, y: y - handleSize - halfStrokeWidth };
				secondPoint = { x: x - halfStrokeWidth, y: y - halfStrokeWidth };
				thirdPoint = { x: x - handleSize - halfStrokeWidth, y: y - halfStrokeWidth };
				break;
			default:
				firstPoint = thirdPoint = { x, y };
				break;
		}

		const handlerPath = `M ${firstPoint.x} ${firstPoint.y}
                           L ${secondPoint.x} ${secondPoint.y}
                           L ${thirdPoint.x} ${thirdPoint.y}`;

		const dragResult: IDragResults = this.meta(CsiDrag)
			.get(key);
		this._onPointerDrag(dragResult);

		return (
			<g>
				{/* White thick handler to show as white outline */}
				<path
					d={`${handlerPath}`}
					fill="none"
					key={key}
					pointer-events="visible"
					stroke="#ffffff"
					stroke-linecap="square"
					stroke-linejoin="miter"
					stroke-width={`${getZoomValue(strokeWidth)}`}
					styles={{ cursor: "crosshair" }}
				/>
				{/* Black thin handler to show as fill. Drawn using the same path but 2 stroke less */}
				<path
					d={`${handlerPath}`}
					fill="none"
					key={key}
					pointer-events="visible"
					stroke="#000000"
					stroke-linecap="square"
					stroke-linejoin="miter"
					stroke-width={`${getZoomValue(strokeWidth - 2)}`}
					styles={{ cursor: "crosshair" }}
				/>

			</g>
		);
	}
}

export default CroppingHandler;
